<script lang="ts" setup>
import AdDetail from '../components/AdDetail.vue'
import { useRoute } from 'vue-router'
import { ref, computed } from 'vue'
import type { AdDetail as AdDetailType } from '@/types'

// Vue Routerからパラメータとクエリを取得する
const route = useRoute()
const id: string = route.params.appId ? route.params.appId.toString() : ''
const grid = (route.query.grid as string) || ''
// URLクエリのdevパラメータが'true'の場合、devモードとする
const devMode: boolean = route.query.dev === 'true'

// APIから取得したデータを格納するリアクティブな変数
const adDetail = ref<Partial<AdDetailType>>({})

// 初期化されたadDetailがコンポーネントプロパティの要件を満たすか確認するための算出プロパティ
const isAdDetailReady = computed(() => 
  adDetail.value.sitename !== undefined && 
  adDetail.value.lead_image !== undefined &&
  adDetail.value.lead_wordings !== undefined &&
  adDetail.value.footer_wordings !== undefined &&
  adDetail.value.conversion_button_image !== undefined &&
  adDetail.value.appearance !== undefined &&
  adDetail.value.sites !== undefined
)
// エラー状態を管理するための変数（trueの場合はNot foundを表示）
const error = ref<boolean>(false)

// エンドポイントの設定
// devモードの場合はローカルエンドポイントを、そうでない場合は本番のエンドポイントを使用する
const baseEndpoint = devMode
  ? "http://localhost:8080/um_dataset/"
  : "https://apledge.adbroadsys.com/um_dataset/"

// APIでデータを取得するためのソースパスを生成
const sourcePath = `${baseEndpoint}${id}/`

// APIレスポンス型の確認と安全な型変換を行う関数
// AdDetailTypeのすべての必須フィールドが存在するか確認
const validateAdDetail = (data: unknown): AdDetailType => {
  // 型チェック（本来はより厳密なバリデーションが望ましい）
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid data format: Expected an object')
  }
  
  const obj = data as Record<string, unknown>;
  
  // 必須フィールドの存在チェック
  const requiredFields = ['sitename', 'lead_image', 'lead_wordings', 'footer_wordings', 
                          'conversion_button_image', 'appearance', 'sites'];
  
  for (const field of requiredFields) {
    if (obj[field] === undefined) {
      throw new Error(`Required field '${field}' is missing in data`)
    }
  }
  
  return obj as AdDetailType
}

// detail.jsonを取得し、取得したデータをadDetailに格納する
fetch(sourcePath + "detail.json")
  .then(response => {
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }
    return response.text()
  })
  .then(text => {
    // テキスト内の{{source}}を実際のソースパスに置換する
    const modifiedText = text.replace(/{{source}}/g, sourcePath)
    return JSON.parse(modifiedText)
  })
  .then(data => {
    // 取得したデータを型チェックして設定する
    const validatedData = validateAdDetail(data)
    adDetail.value = validatedData
    // assetPathをエンドポイントとIDから設定する
    adDetail.value.assetPath = sourcePath
    // ページタイトルを広告のサイト名に設定する
    if (adDetail.value.sitename) {
      document.title = adDetail.value.sitename
    }
  })
  .catch(err => {
    console.error('Error:', err)
    // エラーが発生した場合、errorフラグをtrueに設定し、ページタイトルを「Not found」に変更する
    error.value = true
    document.title = "Not found"
  })
</script>

<template>
  <!-- エラーが発生した場合は「Not found」を表示 -->
  <div v-if="error" class="message">
    <p>Not found</p>
  </div>
  <!-- エラーがない場合 -->
  <template v-else>
    <!-- adDetailが完全に初期化されている場合、AdDetailコンポーネントを表示する -->
    <template v-if="isAdDetailReady">
      <AdDetail :adDetail="adDetail as AdDetailType" :groupId="grid" />
    </template>
    <!-- adDetailがまだ取得できていない場合、ローディングメッセージを表示する -->
    <template v-else>
      <div class="message">
        <p>now loading...</p>
      </div>
    </template>
  </template>
  <!-- devモードの場合、デバッグ表示を追加する -->
  <div v-if="devMode" class="debug mode-dev">devモード: {{ id }}</div>
</template>

<style scoped lang="scss">
.message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: #666;
}
.debug {
  position: fixed;
  bottom: 0;
  right: 0;
  padding: 4px 8px;
  background-color: #f00;
  color: #fff;
  font-size: 12px;
  z-index: 1000;
  opacity:0.5;
}
</style>
